// ─────────────────────────────────────────────────────────────
// CardDetailDialog — full-screen panel for viewing and editing a card.
// Opens when a card is clicked from any view.
// ─────────────────────────────────────────────────────────────

import { useState, useCallback, useEffect, type ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import type {
  FeatureCard,
  CardUpdatePayload,
  CardStatus,
  CardSource,
  KanbanColumn,
  PriorityLevel,
  ValueLevel,
} from "../types";
import { cardsApi, tagsApi } from "../api/client";
import { StatusBadge, PriorityBadge } from "./CardBadges";

interface CardDetailDialogProps {
  card: FeatureCard;
  open: boolean;
  onClose: () => void;
  onUpdated: (card: FeatureCard) => void;
  onDeleted?: (id: number) => void;
}

const STATUSES: CardStatus[] = ["Idea", "Ready", "Candidate", "In Sprint", "Done"];
const KANBAN_COLUMNS: KanbanColumn[] = ["Candidate Pool", "Committed", "Done"];
const SOURCES: CardSource[] = [
  "Customer Request",
  "Internal Idea",
  "Support Ticket",
  "Bug Report",
  "Research",
  "Other",
];
const PRIORITIES: PriorityLevel[] = ["Low", "Medium", "High", "Urgent"];
const VALUES: ValueLevel[] = ["Nice to Have", "Low", "Medium", "High", "Critical"];

export function CardDetailDialog({
  card,
  open,
  onClose,
  onUpdated,
  onDeleted,
}: CardDetailDialogProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Suggested tags from the API
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);

  useEffect(() => {
    tagsApi.suggested().then((res) => setSuggestedTags(res.tags)).catch(() => {});
  }, []);

  // Editable field state — initialized from card prop
  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description ?? "");
  const [status, setStatus] = useState<CardStatus>(card.status);
  const [priority, setPriority] = useState<PriorityLevel>(card.priority ?? "Medium");
  const [value, setValue] = useState<ValueLevel>(card.value ?? "Medium");
  const [source, setSource] = useState<CardSource>(card.source);
  const [notes, setNotes] = useState(card.notes ?? "");
  const [kanbanColumn, setKanbanColumn] = useState<KanbanColumn | "">(card.kanban_column ?? "");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>(card.tags);

  const resetFields = useCallback(() => {
    setTitle(card.title);
    setDescription(card.description ?? "");
    setStatus(card.status);
    setPriority(card.priority ?? "Medium");
    setValue(card.value ?? "Medium");
    setSource(card.source);
    setNotes(card.notes ?? "");
    setKanbanColumn(card.kanban_column ?? "");
    setTags(card.tags);
    setTagInput("");
    setError(null);
  }, [card]);

  const handleCancel = () => {
    resetFields();
    setEditing(false);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: CardUpdatePayload = {
        title: title.trim(),
        description: description.trim() || null,
        status,
        kanban_column: (kanbanColumn as KanbanColumn) || null,
        priority,
        value,
        source,
        notes: notes.trim() || null,
        tags,
      };
      const updated = await cardsApi.update(card.id, payload);
      onUpdated(updated);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleUpvote = async () => {
    try {
      const result = await cardsApi.upvote(card.id);
      onUpdated({ ...card, upvotes: result.upvotes });
    } catch {
      // silently fail — upvote is non-critical
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${card.title}"? This cannot be undone.`)) return;
    try {
      await cardsApi.delete(card.id);
      onDeleted?.(card.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  };

  const addTag = (tag?: string) => {
    const t = (tag ?? tagInput).trim();
    if (t && !tags.includes(t)) {
      setTags((prev) => [...prev, t]);
    }
    if (!tag) setTagInput("");
  };

  const removeTag = (tag: string) => setTags((prev) => prev.filter((t) => t !== tag));

  // Tags not yet added — shown as clickable chips in edit mode
  const availableSuggestions = suggestedTags.filter((t) => !tags.includes(t));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: "60vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          pb: 1,
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
          <StatusBadge status={card.status} />
          <PriorityBadge priority={card.priority ?? "Medium"} />
          {editing ? (
            <TextField
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              variant="standard"
              sx={{ flex: 1 }}
              inputProps={{ style: { fontSize: "1rem", fontWeight: 500 } }}
            />
          ) : (
            <Typography variant="h3" component="span" sx={{ flex: 1, minWidth: 0 }} noWrap>
              {card.title}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Tooltip title="Upvote">
            <IconButton size="small" onClick={handleUpvote}>
              <ThumbUpOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            {card.upvotes}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close dialog">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 3 }}>
          {/* Main column */}
          <Box>
            <Typography variant="overline" color="text.secondary" gutterBottom>
              Description
            </Typography>
            {editing ? (
              <TextField
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                minRows={4}
                fullWidth
                placeholder="What is this feature? Who does it help? What problem does it solve?"
              />
            ) : (
              <Typography
                variant="body2"
                color={description ? "text.primary" : "text.secondary"}
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {description || "No description provided."}
              </Typography>
            )}

            {/* Tags */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="overline" color="text.secondary" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: editing ? 1 : 0 }}>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onDelete={editing ? () => removeTag(tag) : undefined}
                    sx={{ borderRadius: "3px", fontSize: "0.75rem" }}
                  />
                ))}
                {tags.length === 0 && !editing && (
                  <Typography variant="caption" color="text.secondary">
                    No tags
                  </Typography>
                )}
              </Box>
              {editing && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                      placeholder="Add custom tag…"
                      size="small"
                      sx={{ width: 180 }}
                    />
                    <Button onClick={() => addTag()} size="small">Add</Button>
                  </Box>
                  {availableSuggestions.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {availableSuggestions.map((t) => (
                        <Chip
                          key={t}
                          label={t}
                          size="small"
                          variant="outlined"
                          onClick={() => addTag(t)}
                          sx={{
                            borderRadius: "3px",
                            fontSize: "0.65rem",
                            cursor: "pointer",
                            opacity: 0.7,
                            "&:hover": { opacity: 1 },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Notes */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="overline" color="text.secondary" gutterBottom>
                Notes / Learnings
              </Typography>
              {editing ? (
                <TextField
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  placeholder="Post-completion insights, blockers, decisions…"
                />
              ) : (
                <Typography
                  variant="body2"
                  color={notes ? "text.primary" : "text.secondary"}
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {notes || "No notes."}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Sidebar metadata column */}
          <Box sx={{ borderLeft: "1px solid", borderColor: "divider", pl: 2 }}>
            <MetaField label="Status">
              {editing ? (
                <FormControl fullWidth size="small">
                  <Select value={status} onChange={(e) => setStatus(e.target.value as CardStatus)}>
                    {STATUSES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body2">{card.status}</Typography>
              )}
            </MetaField>

            <MetaField label="Board Column">
              {editing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={kanbanColumn}
                    onChange={(e) => setKanbanColumn(e.target.value as KanbanColumn | "")}
                    displayEmpty
                  >
                    <MenuItem value="">Not on board</MenuItem>
                    {KANBAN_COLUMNS.map((col) => (
                      <MenuItem key={col} value={col}>{col}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body2" color={card.kanban_column ? "text.primary" : "text.secondary"}>
                  {card.kanban_column ?? "Not on board"}
                </Typography>
              )}
            </MetaField>

            <MetaField label="Priority">
              {editing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as PriorityLevel)}
                  >
                    {PRIORITIES.map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <PriorityBadge priority={card.priority ?? "Medium"} />
              )}
            </MetaField>

            <MetaField label="Value">
              {editing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={value}
                    onChange={(e) => setValue(e.target.value as ValueLevel)}
                  >
                    {VALUES.map((v) => (
                      <MenuItem key={v} value={v}>{v}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body2">{card.value ?? "Medium"}</Typography>
              )}
            </MetaField>

            <MetaField label="Source">
              {editing ? (
                <FormControl fullWidth size="small">
                  <Select value={source} onChange={(e) => setSource(e.target.value as CardSource)}>
                    {SOURCES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body2">{card.source}</Typography>
              )}
            </MetaField>

            {card.submitted_by && (
              <MetaField label="Submitted By">
                <Typography variant="body2">{card.submitted_by}</Typography>
              </MetaField>
            )}

            <MetaField label="Customer Requests">
              <Typography variant="body2">{card.customer_request_count}</Typography>
            </MetaField>

            <MetaField label="Created">
              <Typography variant="body2">
                {new Date(card.created_at).toLocaleDateString()}
              </Typography>
            </MetaField>

            {card.completed_at && (
              <MetaField label="Completed">
                <Typography variant="body2">
                  {new Date(card.completed_at).toLocaleDateString()}
                </Typography>
              </MetaField>
            )}
          </Box>
        </Box>

        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 1.5 }}>
        <Box>
          {onDeleted && (
            <Button
              color="error"
              variant="outlined"
              size="small"
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {editing ? (
            <>
              <Button variant="outlined" size="small" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={12} /> : undefined}
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" size="small" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ── Internal helper ──────────────────────────────────────────

function MetaField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.4, mb: 0.25 }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}
